import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LoginResponse } from '../types';
import { authService } from '../services/authService';
import api from '../services/api';

interface AuthContextType {
  user: LoginResponse | null;
  isAuthenticated: boolean;
  login: (email: string, senha: string) => Promise<LoginResponse>;
  logout: () => void;
  switchTenant: (tenantId: number) => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const semOrganizacaoAtiva = (usuario: LoginResponse) =>
  usuario.perfil !== 'SUPER_ADMIN' && (usuario.tenants?.length ?? 0) === 0;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<LoginResponse | null>(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    if (savedUser && savedToken) {
      api.get('/auth/me')
        .then((response) => {
          const dados = response.data;
          const validUser: LoginResponse = {
            ...JSON.parse(savedUser),
            id: dados.id,
            motoristaId: dados.motoristaId,
            nome: dados.nome,
            email: dados.email,
            perfil: dados.perfil,
            tenants: dados.tenants || [],
            tenantIdSelecionado: dados.tenantIdSelecionado,
            tenantNome: dados.tenantNome,
          };
          if (semOrganizacaoAtiva(validUser)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            return;
          }
          localStorage.setItem('user', JSON.stringify(validUser));
          setUser(validUser);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        });
    }
  }, []);

  const login = async (email: string, senha: string): Promise<LoginResponse> => {
    const response = await authService.login({ email, senha });
    if (semOrganizacaoAtiva(response)) {
      throw new Error('Usuário não está vinculado a nenhuma organização ativa');
    }
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response));
    setUser(response);
    return response;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const switchTenant = async (tenantId: number) => {
    const response = await api.post<LoginResponse>('/auth/switch-tenant', { tenantId });
    const data = response.data;
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
  };

  const hasRole = (role: string) => {
    return user?.perfil === role;
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, logout, switchTenant, hasRole }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
